import React, { useMemo } from 'react';
import Lightbox, { Slide } from 'yet-another-react-lightbox';
import Inline from 'yet-another-react-lightbox/plugins/inline';
import * as S from '@/components/Slider/Slider.styles';
import { AdKind } from '@/api/types/common.types';
import { KindIcons } from '@/components/KindIcons/KindIcons';
import 'yet-another-react-lightbox/styles.css';
import { PhotoOverlay } from '@/components/PhotoOverlay';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';

interface SliderProps {
  images: string[];
  title?: string;
  kinds?: AdKind[];
  openGallery?: () => void;
  sliderId: string;
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  dataStat?: string;
  loading?: boolean;
  overlayText?: string;
}

type CustomSlide = Slide & { index: number };

export const Slider: React.FC<SliderProps> = ({
  images,
  title = 'Slider',
  kinds,
  openGallery,
  sliderId,
  currentIndex,
  setCurrentIndex,
  dataStat,
  overlayText,
  loading = false,
}) => {
  const totalSlides = images.length;
  const slidesCounter = `${currentIndex + 1} / ${totalSlides}`;

  const sliderProps = useMemo(
    () => ({
      carousel: {
        padding: 0,
        spacing: 0,
        preload: 2,
        imageFit: 'cover' as const,
      },
      inline: {
        style: {
          width: '100%',
          height: '330px',
        },
      },
      styles: { container: { backgroundColor: 'transparent' } },
      render: {
        buttonPrev: () => null,
        buttonNext: () => null,
        buttonClose: () => null,
        slide: ({ slide }: { slide: Slide }) => {
          const { src, index } = slide as CustomSlide;

          return (
            <S.Img
              data-testid={AdPageTestIds.slider.image}
              data-stat={dataStat}
              src={src}
              alt={title + (index ? ` (${index + 1})` : '')}
              onClick={openGallery}
              {...(index ? { loading: 'lazy' } : { fetchpriority: 'high' })}
            />
          );
        },
      },
    }),
    [dataStat, openGallery]
  );

  const slides = useMemo(
    () => images.map((src, index) => ({ src, index })),
    [images]
  );

  return (
    <S.Container id={sliderId} $loading={loading}>
      {!!overlayText && <PhotoOverlay overlayText={overlayText} zIndex={2} />}

      <Lightbox
        index={currentIndex}
        slides={slides}
        plugins={[Inline]}
        on={{ view: ({ index }) => setCurrentIndex(index) }}
        {...sliderProps}
      />

      <KindIcons kinds={kinds} />

      <S.Count data-testid={AdPageTestIds.slider.counter}>
        {slidesCounter}
      </S.Count>
    </S.Container>
  );
};
